import { motion } from 'framer-motion';
import { Brain, Globe, ShieldCheck, Lock, KeyRound, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';


const aiLabData = {
  badge: {
    text: "Optional",
    variant: "secondary" as const
  },
  icon: Brain,
  iconColor: "bg-emerald-500",
  heading: "Layer in AI Lab",
  subheadline: "AI that learns YOUR brand, YOUR way",
  tagline: "Custom AI agents, integrations with your tools, and expert help to connect everything to your unique and proven workflow. No tech overhead, no retraining.",
  features: ["Connected to your knowledge base", "Custom agents and workflows", "Integrations with your tools", "Always updated, no tech overhead, no re-training"],
  buttonText: "Learn About AI Lab",
  buttonLink: "/enterprise#ai-lab"
};

const trustBadges = [
  { 
    icon: Globe, 
    label: "Global Reach", 
    description: "Worldwide infrastructure",
    gradient: "from-blue-500/20 to-cyan-500/20",
    iconBg: "bg-blue-500",
    border: "border-blue-500/30",
  },
  { 
    icon: ShieldCheck, 
    label: "SOC-2", 
    description: "Type II certified",
    gradient: "from-emerald-500/20 to-green-500/20",
    iconBg: "bg-emerald-500",
    border: "border-emerald-500/30",
  },
  { 
    icon: Lock, 
    label: "GDPR", 
    description: "Fully compliant",
    gradient: "from-purple-500/20 to-violet-500/20",
    iconBg: "bg-purple-500",
    border: "border-purple-500/30",
  },
  { 
    icon: KeyRound, 
    label: "End-to-End Encryption", 
    description: "Always protected",
    gradient: "from-orange-500/20 to-amber-500/20",
    iconBg: "bg-orange-500",
    border: "border-orange-500/30",
  },
];

const AILabSection = () => {
  return (
    <section className="relative py-16 sm:py-20 lg:py-24 bg-muted/30 overflow-hidden">
      {/* Background rotating shield accent */}
      <motion.div
        className="absolute top-20 right-10 text-emerald-200/20 hidden lg:block"
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
      >
        <Shield className="w-32 h-32" />
      </motion.div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }} 
          transition={{ duration: 0.5 }} 
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start max-w-6xl mx-auto"
        >
          {/* Left Column - Content */}
          <div className="text-center lg:text-left">
            <Badge variant="secondary" className="w-fit mb-4 mx-auto lg:mx-0">
              {aiLabData.badge.text}
            </Badge>

            {/* Header with icon always on left of title */}
            <div className="flex items-start gap-3 sm:gap-4 mb-4 justify-center lg:justify-start">
              <div className="relative w-12 h-12 flex-shrink-0">
                {/* Pulse glow behind Brain icon */}
                <motion.div
                  className="absolute inset-0 bg-emerald-400/30 rounded-xl blur-md"
                  animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
                <div className={`relative w-12 h-12 ${aiLabData.iconColor} rounded-xl flex items-center justify-center`}>
                  <Brain className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="text-left">
                <h3 className="text-2xl lg:text-3xl font-bold text-foreground">
                  {aiLabData.heading}
                </h3>
                <p className="text-base lg:text-lg text-secondary-foreground mt-1">
                  {aiLabData.subheadline}
                </p>
              </div>
            </div>

            {/* Tagline */}
            <p className="mb-6 text-secondary-foreground text-base lg:text-lg text-center lg:text-left">
              {aiLabData.tagline}
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap gap-2 mb-8 justify-center lg:justify-start">
              {aiLabData.features.map((feature, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.1 }}
                  className="px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium"
                >
                  {feature}
                </motion.span>
              ))}
            </div>

            {/* CTA */}
            <div className="flex justify-center lg:justify-start">
              <Button variant="default" asChild>
                <Link to={aiLabData.buttonLink}>{aiLabData.buttonText}</Link>
              </Button>
            </div>
          </div>

          {/* Right Column - Trust Badges */}
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              {trustBadges.map((badge, index) => {
                const Icon = badge.icon;
                return (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    animate={{ x: [0, index % 2 === 0 ? 2 : -2, 0], y: [0, index < 2 ? -2 : 2, 0] }}
                    whileHover={{ scale: 1.03, y: -2 }}
                    className={`p-4 rounded-xl bg-gradient-to-br ${badge.gradient} border ${badge.border} flex flex-col items-center justify-center text-center min-h-[140px] cursor-default transition-shadow duration-200 hover:shadow-md`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${badge.iconBg} flex items-center justify-center mb-2`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-semibold text-foreground text-sm">{badge.label}</span>
                    <span className="text-xs text-muted-foreground mt-1">{badge.description}</span>
                  </motion.div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Your data stays yours. SOC-2 compliant. End-to-end encryption on all workflows.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AILabSection;
